import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import "@glideapps/glide-data-grid/dist/index.css";
import AppProviders from "AppProviders";
import AppRouter from "AppRouter";
import axios from "axios";
import { IS_AWS_ENV, IS_PROD_ENV } from "constants/app.constants";
import { cookies } from "constants/settings.constants";
import {
  logout,
  setUser,
  updateJwtToken,
  updateRefresingTokenLock,
  updateTokenFromRefreshToken
} from "store/features";
import { RootState } from "store/rootReducer";
import { checkTokenExpire, decrypt } from "utils";
import { broadcast, broadcastTypes } from "utils/broadcast";
import { initializeEvaUnitCore } from "utils/evaUnitCoreInitializer";
import { publicUrl } from "utils/publicUrl";

import useOnWindowFocus from "hooks/useOnWindowFocus";
import { useSentryUserContext } from "hooks/useSentryUserContext";
import { useSystemStatusRedux } from "hooks/useSystemStatusRedux";

import { getCurrentUser, refreshToken, signoutUser } from "api/auth";

import { IUser } from "models/User";

import type { AutomationBroker } from "components/automation/contexts/AutomationBrokerContext";
import { SystemStatusBanner } from "components/system-status";

import "./App.css";

let tokenInterval: NodeJS.Timeout = null;
let hasInitializedEvaUnitCore = false; // Track if eva-unit-core has been initialized
declare global {
  interface Window {
    AUTOMATION?: boolean;
    broker?: AutomationBroker;
    updateUser?: (user: IUser) => void;
  }
}
function App(): JSX.Element {
  const dispatch = useDispatch();

  const user = useSelector((state: RootState) => state.auth.user);
  const isTokenRefreshing = useSelector((state: RootState) => state.auth.tokenRefreshing);
  const currentJwtToken = useSelector((state: RootState) => state.auth.jwtToken);

  const history = useHistory();
  //tells the loading screen to redirect to either /app or /login
  const [doneLoading, setDoneLoading] = useState(false);
  const automationUser =
    process.env.REACT_APP_AUTOMATION_USER || "automation@turinganalytics.net";

  // Initialize Sentry user context
  useSentryUserContext();

  // Centralized system status polling - only one instance polls
  useSystemStatusRedux({
    pollingInterval: 5 * 60 * 1000, // 5 minutes
    enabled: !!user && doneLoading // Only poll when user is authenticated and app is loaded
  });

  useEffect(() => {
    const isAutomationWindow = window.AUTOMATION; //backend will set AUTOMATION to true;
    const isProd = IS_PROD_ENV;
    const isAutomationUser = user?.username && user?.username == automationUser;
    const exposeToAutomation = isAutomationUser && isAutomationWindow && !isProd;

    if (exposeToAutomation) {
      window.updateUser = (user: IUser) => {
        dispatch(setUser(user));
        dispatch(updateJwtToken(user.jwtToken));
      };
    }
    return () => {
      delete window.updateUser;
    };
  }, [user]);

  // SSO bridge: the edge (Cognito) owns the session and mints a JWT-only token
  // with no EVA refresh cookie. EVA's own refresh-token / sign-out machinery must
  // not drive renewal here. Gate the renewal + expiry paths on this flag and
  // re-mint via GET /current (which issues a fresh 20-min token) instead.
  // Read at render (not via IS_SSO_BRIDGE from app.constants) so App.test.tsx
  // can toggle the env var per test; Vite inlines the env either way in builds.
  const ssoBridge = process.env.REACT_APP_SSO_BRIDGE === "true";

  // Re-mint a fresh JWT via the bridge bootstrap endpoint. Returns true when the
  // session was rehydrated. Used for both proactive (pre-expiry) renewal and the
  // reactive (401) fallback. No cross-tab refresh lock needed: the bridge does no
  // refresh-token rotation, so there is no cross-tab race to coordinate.
  const rehydrateFromBridge = async (): Promise<boolean> => {
    const currentUser = await getCurrentUser();
    if (currentUser) {
      dispatch(setUser(currentUser));
      return true;
    }
    return false;
  };

  const updateFromRefreshResponse = async (newUser) => {
    if (newUser) {
      dispatch(setUser(newUser));
    } else if (ssoBridge) {
      // bridge mode: never force EVA /login on a refresh miss — the edge re-auths.
      return;
    } else {
      //sign out
      await signoutUser(user?.username);
      dispatch(logout());

      window.location.href = publicUrl("login");
    }
  };

  const getAppTitle = () => {
    switch (process.env.REACT_APP_ENV) {
      case "local":
        return "EVA (Local)";
      case "mcdan":
        return "Aurora";
      case "research":
        return "Research EVA";
      case "gather":
        return "Gather EVA";
      case "dev":
        return "Dev EVA";
      // The AWS environments are where EVA is presented as part of the Novi
      // suite, so the tab is named the way the other Novi apps name theirs.
      case "dev-aws":
        return "Novi EVA | Dev";
      case "prod-aws":
        return "Novi EVA";
      default:
        return "EVA";
    }
  };

  /**
   * Swaps the tab icon for the Novi mark in the AWS environments, the same one
   * the other Novi apps wear.
   *
   * Done here rather than in index.html because that file is static and serves
   * every environment; the tab's name is already set from `REACT_APP_ENV` a few
   * lines below, so its icon follows the same rule from the same place. Outside
   * AWS nothing happens and the EVA favicon from index.html stands.
   */
  const applyNoviFavicon = () => {
    if (!IS_AWS_ENV) return;

    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) return;

    link.type = "image/png";
    link.href = publicUrl("novi-favicon.png");
  };

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response && error.response.status === 401 && !isTokenRefreshing) {
          if (ssoBridge) {
            // bridge mode: re-mint via /current (no EVA refresh cookie exists).
            // Recursion guard: a 401 ON /current must not re-enter this branch
            // (getCurrentUser rides this same axios instance). If /current can't
            // rehydrate, the Cognito session is gone — reload so the edge
            // re-authenticates (it owns the redirect to Novi auth).
            const reqUrl = error.config?.url ?? "";
            if (!reqUrl.includes("/current")) {
              const rehydrated = await rehydrateFromBridge();
              if (!rehydrated) {
                window.location.reload();
              }
            }
          } else if (user) {
            //unauthroized so we try to get a new token from refresh token
            const newUser = await refreshToken(user?.sessionBroadcastToken);
            updateFromRefreshResponse(newUser);
          } else if (history) {
            history.push("/login");
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, [history, user, isTokenRefreshing]);

  useEffect(() => {
    const handleBroadcastMessage = async (event) => {
      switch (event.data.type) {
        case broadcastTypes.TOKEN_UPDATED:
          if (event.data.encrypted && event.data.iv && user?.sessionBroadcastToken) {
            // only decrypt if the sessionBroadcastToken is available
            const deData = await decrypt(
              { encrypted: event.data.encrypted, iv: event.data.iv },
              user.sessionBroadcastToken
            );
            const newUser: IUser = JSON.parse(deData);
            updateFromRefreshResponse(newUser);
          }
          break;
        case broadcastTypes.TOKEN_REFRESHING:
          {
            const lock = event.data.tokenRefreshing;
            dispatch(updateRefresingTokenLock(lock));
          }
          break;
      }
    };

    // Listen for token updates from other tabs
    broadcast.addEventListener("message", handleBroadcastMessage);

    return () => {
      // Remove the event listener on cleanup
      broadcast.removeEventListener("message", handleBroadcastMessage);
    };
  }, [user]);

  // Get Token on load
  useEffect(() => {
    // Set the browser tab title and icon based on environment
    document.title = getAppTitle();
    applyNoviFavicon();

    const refreshTokenOnExpiry = async () => {
      const lastLoggedIn = localStorage.getItem(cookies.LAST_LOGGED_IN);

      // SSO bridge: the edge delivers the JWT as an HttpOnly cookie, so there is no
      // client-stored session to detect. Hydrate by verifying against the backend
      // (the edge presents the cookie as the Authorization header). Bridge-only, so the
      // normal web-login / SAML-SSO bootstrap below is unchanged. We deliberately do NOT
      // fall through to refreshToken() here — the bridge sets no refresh cookie.
      if (
        process.env.REACT_APP_SSO_BRIDGE === "true" &&
        !user &&
        !window.location.href.includes("/login")
      ) {
        const currentUser = await getCurrentUser();
        if (currentUser) {
          dispatch(setUser(currentUser));
        }
        setDoneLoading(true);
        return;
      }

      if (!lastLoggedIn || window.location.href.includes("/login") || isTokenRefreshing) {
        setDoneLoading(true);
        return;
      }

      const newUser = await refreshToken(user?.sessionBroadcastToken);
      updateFromRefreshResponse(newUser);

      setDoneLoading(true);
    };

    refreshTokenOnExpiry();
  }, []);

  // Initialize eva-unit-core after authentication (when JWT token is available for the first time)
  useEffect(() => {
    if (!currentJwtToken || hasInitializedEvaUnitCore) return; // Wait for authentication and only run once

    const initializeWasm = async () => {
      try {
        await initializeEvaUnitCore();
        hasInitializedEvaUnitCore = true;
      } catch (error) {
        Sentry.captureException(error);
        Sentry.logger.error("Failed to initialize eva-unit-core", { error });
      }
    };

    initializeWasm();
  }, [currentJwtToken]); // Run when JWT token becomes available

  async function updateTokenIfAboutToExpire(jwtToken) {
    if (!currentJwtToken) {
      return;
    }

    // Check if token is about to expire
    if (checkTokenExpire(jwtToken)) {
      if (ssoBridge) {
        // bridge mode: re-mint via /current before expiry. The refresh-token path
        // would 400 (no refresh cookie) and sign the user out after ~15 min.
        await rehydrateFromBridge();
      } else {
        dispatch(updateTokenFromRefreshToken(user.username));
      }
    }
  }

  // Check for token expiry on window focus
  useOnWindowFocus(() => {
    if (currentJwtToken) {
      updateTokenIfAboutToExpire(currentJwtToken);
    }
  });

  useEffect(() => {
    if (tokenInterval) {
      clearInterval(tokenInterval);
      tokenInterval = null;
    }

    //run every minute
    tokenInterval = setInterval(async () => {
      updateTokenIfAboutToExpire(currentJwtToken);
    }, 60000);

    return () => {
      if (tokenInterval) {
        clearInterval(tokenInterval);
        tokenInterval = null;
      }
    };
  }, [currentJwtToken, dispatch]);

  return (
    <AppProviders>
      <div className="App">
        <AppRouter doneLoading={doneLoading} />
        <ToastContainer
          position="top-center"
          autoClose={5000}
          closeButton={false}
          limit={1}
          enableMultiContainer
          pauseOnHover
        />
        <SystemStatusBanner />
      </div>
    </AppProviders>
  );
}

export default App;
